import { Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressInput } from '../../../components/Form/AddressInput/AddressInput';
import {
	GenericInput,
	GenericInputProps,
} from '../../../components/Form/GenericInput/GenericInput';
import { GenericSelect } from '../../../components/Form/GenericSelect/GenericSelect';
import NavigationBar from '../../../components/NavigationBar';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { MONTHS } from '../../../lib/constants';
import { Address } from '../../../lib/services/googlemaps/Address';
import { useUniversalTracking } from '../../../lib/tracking/universalTracking';
import { dateFromComponents, getDateComponent } from '../../../lib/utils';
import { colors } from '../../../theme/theme';
import { ContractState } from '../../../types/redux';
import {
	onPressEnterHandlerFactory,
	onSelectInputHandlerFactory,
} from '../../../utils/formInteraction';

const useStyles = makeStyles((theme: Theme) => ({
	sectionTitle: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	generalErrorMessage: {
		color: colors.red,
		fontSize: 14,
		marginTop: theme.spacing(1),
		padding: theme.spacing(1, 1, 1, 0),
	},

	'.user_data_selector_wrapper': {
		display: 'flex',
		width: '153px',
		height: '56px',
		backgroundColor: 'white',
		marginRight: '24px',
		marginBottom: '10px',
	},
	'.user_data_group_wrapper': {
		display: 'flex',
		flexWrap: 'wrap',
		marginBottom: '10px',
	},
	'@media screen and (max-width: 585px)': {
		'.user_data_group_wrapper': {
			width: '316px',
			justifyContent: 'space-between',
		},
		'.user_data_input': {
			marginRight: '0px',
		},
	},
	'.user_data_input': {
		height: '56px',
		maxHeight: '56px',
		marginRight: '24px',
		marginBottom: '24px',
	},
	'.user_data_big_input_wrapper': {
		width: '316px',
		maxWidth: '316px',
	},
	'.user_data_small_input_wrapper': {
		width: '220px',
		maxWidth: '220px',
	},
	'.user_data_very_small_input_wrapper': {
		width: '65px',
		maxWidth: '65px',
	},
	'.zip_code_input_wrapper': {
		width: '80px',
		maxWidth: '80px',
	},
	'.user_data_price_continue_wrapper': {
		display: 'flex',
		marginTop: '32px',
	},
	'.user_data_birthday_error': {
		color: '#d63541',
		fontSize: '14px',
		lineHeight: '14px',
		marginLeft: '10px',
		marginTop: '-10px',
	},
}));

interface Props extends StateProps, DispatchProps {
	onSubmit(data: any): void;
}

const UserData: FunctionComponent<Props> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const [idNumber, setIdNumber] = useState(props.contract.ausweisId);
	const [surname, setSurname] = useState(props.contract.name);
	const [name, setName] = useState(props.contract.vorname);
	const [street, setStreet] = useState(props.contract.street);
	const [streetNumber, setStreetNumber] = useState(props.contract.streetNumber);
	const [city, setCity] = useState(props.contract.ort);
	const [zipCode, setZipCode] = useState(props.contract.plz);
	const [birthYear, setBirthYear] = useState(
		getDateComponent(props.contract.geburtsdatum, 'year')
	);
	const [birthMonth, setBirthMonth] = useState(
		getDateComponent(props.contract.geburtsdatum, 'month')
	);
	const [birthDay, setBirthDay] = useState(
		getDateComponent(props.contract.geburtsdatum, 'day')
	);

	const [surnameError, setSurnameError] = useState(undefined);
	const [nameError, setNameError] = useState(undefined);
	const [streetError, setStreetError] = useState(undefined);
	const [streetNumberError, setStreetNumberError] = useState(undefined);
	const [cityError, setCityError] = useState(undefined);
	const [zipCodeError, setZipCodeError] = useState(undefined);
	const [birthYearError, setBirthYearError] = useState(undefined);
	const [birthMonthError, setBirthMonthError] = useState(undefined);
	const [birthDayError, setBirthDayError] = useState(undefined);
	const [over18Error, setOver18Error] = useState('');
	const [generalError, setGeneralError] = useState('');

	const { trackEvent } = useUniversalTracking();

	const nameRef = React.useRef<HTMLElement>(null);
	const surnameRef = React.useRef<HTMLElement>(null);
	const streetRef = React.useRef<HTMLElement>(null);
	const streetNumberRef = React.useRef<HTMLElement>(null);
	const zipCodeRef = React.useRef<HTMLElement>(null);
	const cityRef = React.useRef<HTMLElement>(null);
	const birthDayRef = React.useRef<HTMLElement>(null);
	const birthMonthRef = React.useRef<HTMLElement>(null);
	const birthYearRef = React.useRef<HTMLElement>(null);
	const formRefs = [
		nameRef,
		surnameRef,
		streetRef,
		streetNumberRef,
		zipCodeRef,
		cityRef,
		birthDayRef,
		birthMonthRef,
		birthYearRef,
	];

	useEffect(
		() => validateNotEmpty(surname, surnameError, setSurnameError),
		// eslint-disable-next-line
		[surname]
	);
	useEffect(
		() => validateNotEmpty(name, nameError, setNameError),
		// eslint-disable-next-line
		[name]
	);
	useEffect(
		() => validateNotEmpty(street, streetError, setStreetError),
		// eslint-disable-next-line
		[street]
	);
	useEffect(
		() => validateNotEmpty(streetNumber, streetNumberError, setStreetNumberError),
		// eslint-disable-next-line
		[streetNumber]
	);
	useEffect(
		() => validateNotEmpty(city, cityError, setCityError),
		// eslint-disable-next-line
		[city]
	);
	useEffect(
		() => validateZipcode(zipCode, zipCodeError, setZipCodeError),
		// eslint-disable-next-line
		[zipCode]
	);
	useEffect(
		() => validateSelectNotEmpty(birthYear, birthYearError, setBirthYearError),
		// eslint-disable-next-line
		[birthYear]
	);
	useEffect(
		() => validateSelectNotEmpty(birthMonth, birthMonthError, setBirthMonthError),
		// eslint-disable-next-line
		[birthMonth]
	);
	useEffect(
		() => validateSelectNotEmpty(birthDay, birthDayError, setBirthDayError),
		// eslint-disable-next-line
		[birthDay]
	);
	useEffect(
		() => validateBirthDate(),
		// eslint-disable-next-line
		[birthDay, birthMonth, birthYear]
	);

	useEffect(() => fillIdNumber());

	const validateAndSubmit = () => {
		// console.error('validateAndSubmit', birthYear);
		// Force validations
		validateNotEmpty(surname, surnameError, setSurnameError, true);
		validateNotEmpty(name, nameError, setNameError, true);
		validateNotEmpty(street, streetError, setStreetError, true);
		validateNotEmpty( streetNumber, streetNumberError, setStreetNumberError,  true);
		validateNotEmpty(city, cityError, setCityError, true);
		validateZipcode(zipCode, zipCodeError, setZipCodeError, true);
		validateSelectNotEmpty(birthYear, birthYearError, setBirthYearError, true);
		validateSelectNotEmpty(birthMonth, birthMonthError, setBirthMonthError,true );
		validateSelectNotEmpty(birthDay, birthDayError, setBirthDayError, true);
		validateBirthDate();

		if (!formIsValid()) {
			setGeneralError('Bitte füllen Sie die Kundendaten vollständig aus.');

			return;
		}

		onSubmit();
	};

	const formIsValid = () => {
		const errors = [
			surnameError,
			nameError,
			streetError,
			streetNumberError,
			cityError,
			zipCodeError,
			birthYearError,
			birthMonthError,
			birthDayError,
			over18Error,
		].filter((errMsg) => errMsg !== '');

		// console.error('formIsValid', errors, birthYear);

		return errors.length === 0;
	};

	const fillIdNumber = () => {
		setIdNumber(`${birthDay}${birthMonth}${birthYear}${zipCode}`);
	};

	const validateBirthDate = () => {
		if (!!birthDay && !!birthDay && !!birthYear) {
			const birthDate = moment(`${birthYear}-${birthMonth}-${birthDay}`);
			if (!birthDate.isValid()) {
				// invalid date
				setOver18Error(t('kppVehicle.errors.invalidDate'));
			} else if (moment().diff(birthDate, 'years') < 18) {
				// younger than 18 years old
				setOver18Error(t('user_data.birthday_error'));
			} else {
				// no errors
				setOver18Error('');
			}
		} else {
			// no validation before input is complete
			setOver18Error('');
		}
	};

	const onSubmit = () => {
		const data = {
			vorname: name,
			name: surname,
			ausweisId: idNumber,
			geburtsdatum: dateFromComponents(birthYear, birthMonth, birthDay),
			street,
			streetNumber,
			strasse: street.concat(' ', streetNumber).trim(),
			plz: zipCode,
			ort: city,
		};

		trackEvent({
			category: 'userData-submit',
			action: 'click-event',
			label: 'userData',
		});
		props.onSubmit(data);
	};

	const validateNotEmpty = (
		value: string,
		valueError: string | undefined,
		setValueError: any,
		forceValidation: boolean = false
	) => {
		// Note: valueError === undefined will stop the validation from happening, we will start
		// validating as soon as length > 0 or an external function sets valueError !== undefined
		if (value.length > 0) {
			setValueError('');
		} else if (forceValidation || valueError !== undefined) {
			// TODO refactor
			// we have an error but don't want to show a message here because we have the general error message
			setValueError(' ');
		}
	};

	const validateZipcode = (
		value: string,
		valueError: string | undefined,
		setValueError: any,
		forceValidation: boolean = false
	) => {
		if (value.length === 5 || value.length === undefined) {
			setValueError('');
		} else if (forceValidation || valueError !== undefined) {
			setValueError('5 Zeichen');
		}
	};

	const validateSelectNotEmpty = (
		value: string,
		valueError: string | undefined,
		setValueError: any,
		forceValidation: boolean = false
	) => {
		// Note: valueError === undefined will stop the validation from happening, we will start
		// validating as soon as length > 0 or an external function sets valueError !== undefined
		if (value !== '') {
			setValueError('');
		} else if (forceValidation || valueError !== undefined) {
			setValueError(' '); // Any non-blank string will highlight the select in red
		}
	};

	const getMonthsSelectValues = () => {
		const values: any[] = [];
		let monthNumber = 1;
		for (const month of MONTHS) {
			values.push({
				value: String(monthNumber).padStart(2, '0'),
				label: t('common.months.' + month),
			});
			monthNumber++;
		}

		return values;
	};
	const getYearSelectValues = () => {
		const values: any[] = [];
		for (let year = new Date().getFullYear(); year > 1920; year--) {
			values.push({ value: year, label: year });
		}

		return values;
	};

	const getDaysSelectValues = () => {
		const values: any[] = [];
		for (let day = 1; day <= 31; day++) {
			values.push({
				value: String(day).padStart(2, '0'),
				label: day,
			});
		}

		return values;
	};

	const monthSelectValues: any[] = getMonthsSelectValues();
	const yearSelectValues: any[] = getYearSelectValues();
	const daysSelectValues: any[] = getDaysSelectValues();

	const forceAlphanumeric = (
		text: string,
		forceMaxLength?: number,
		forceUpperCase?: boolean
	) => {
		let result = text;
		if (forceMaxLength && result.length > forceMaxLength) {
			result = result.slice(0, forceMaxLength);
		}
		// COMMENTED OUT, WE WILL ALLOW ANY CHARACTER HERE
		// text = text.replace(/[^0-9a-zA-ZäöüÄÖÜß\s]/g,'')
		if (forceUpperCase) {
			result = result.toUpperCase();
		}

		return result;
	};

	const zipErrorMessage = () =>
		zipCode.length === 0 || zipCode.length === 5 ? '' : '5 Zeichen';

	// event listener on input field key press events to focus next input or submit form
	const onKeyPressEnter = onPressEnterHandlerFactory(
		formRefs,
		validateAndSubmit
	);

	// event listener on select field input events to focus next input or submit form
	const onSelectInput = onSelectInputHandlerFactory(
		formRefs,
		validateAndSubmit
	);

	const onSelectBirthYear = (val: string) => {
		setBirthYear(val);
		// FIXME: validateAndSubmit() does not have the correct birthYear at this precise moment
		// validateAndSubmit();
	};

	const addressInputProps: GenericInputProps = {
		inputId: 'user_data_street',
		inputValue: street,
		error: streetError,
		setInputValue: (value: any) => {
			setStreet(forceAlphanumeric(value, 28));
		},
		label: t('user_data.street'),
		additionalInputParams: {
			inputRef: streetRef,
			onKeyPress: onKeyPressEnter,
		},
	};

	const onAddressCompleted = (address?: Address): boolean => {
		setStreet(address ? address.street : '');
		setStreetNumber(address ? address.streetNumber : '');
		setZipCode(address ? address.zipCode : '');
		setCity(address ? address.city : '');
		return true;
	};

	return (
		<div>
			<PageTitle title={t('user_data.title')} />
			{!!generalError && (
				<div className={classes.generalErrorMessage}>{generalError}</div>
			)}
			<Typography
				variant="h3"
				className={`${classes.sectionTitle} userDataAddressTitle`}
			>
				{t('user_data.section1_title')}
			</Typography>
			<div className={classes['.user_data_group_wrapper']}>
				<div
					className={`${classes['.user_data_big_input_wrapper']} ${classes['.user_data_input']}`}
				>
					<GenericInput
						inputId="user_data_name"
						inputValue={name}
						error={nameError}
						setInputValue={(value) => {
							setName(forceAlphanumeric(value, 30));
						}}
						label={t('user_data.name')}
						additionalInputParams={{
							inputRef: nameRef,
							onKeyPress: onKeyPressEnter,
							autoFocus: true,
						}}
					/>
				</div>
				<div
					className={`${classes['.user_data_big_input_wrapper']} ${classes['.user_data_input']}`}
				>
					<GenericInput
						inputId="user_data_surname"
						inputValue={surname}
						error={surnameError}
						setInputValue={(value) => {
							setSurname(forceAlphanumeric(value, 30));
						}}
						label={t('user_data.surname')}
						additionalInputParams={{
							inputRef: surnameRef,
							onKeyPress: onKeyPressEnter,
						}}
					/>
				</div>
			</div>
			<div className={classes['.user_data_group_wrapper']}>
				<div className={classes['.user_data_group_wrapper']}>
					<div
						className={`${classes['.user_data_small_input_wrapper']} ${classes['.user_data_input']}`}
					>
						<AddressInput
							nestedInputProps={addressInputProps}
							onAddressCompleted={onAddressCompleted}
						/>
					</div>
					<div
						className={`${classes['.user_data_very_small_input_wrapper']} ${classes['.user_data_input']}`}
					>
						<GenericInput
							inputId="user_data_street_number"
							inputValue={streetNumber}
							error={streetNumberError}
							setInputValue={(value) => {
								setStreetNumber(forceAlphanumeric(value, 5));
							}}
							label={t('user_data.street_number')}
							additionalInputParams={{
								inputRef: streetNumberRef,
								onKeyPress: onKeyPressEnter,
							}}
						/>
					</div>
				</div>
				<div className={classes['.user_data_group_wrapper']}>
					<div
						className={`${classes['.zip_code_input_wrapper']} ${classes['.user_data_input']}`}
					>
						<GenericInput
							inputId="user_data_zip_code"
							inputValue={zipCode}
							setInputValue={(value) => {
								setZipCode(forceAlphanumeric(value, 5));
							}}
							error={zipErrorMessage() || zipCodeError}
							label={t('user_data.zip_code')}
							type="number"
							additionalInputParams={{
								inputRef: zipCodeRef,
								onKeyPress: onKeyPressEnter,
							}}
						/>
					</div>
					<div
						className={`${classes['.user_data_small_input_wrapper']} ${classes['.user_data_input']}`}
					>
						<GenericInput
							inputId="user_data_city"
							inputValue={city}
							error={cityError}
							setInputValue={(value) => {
								setCity(forceAlphanumeric(value, 30));
							}}
							label={t('user_data.city')}
							additionalInputParams={{
								inputRef: cityRef,
								onKeyPress: onKeyPressEnter,
							}}
						/>
					</div>
				</div>
			</div>
			<Typography variant="h3" className={`${classes.sectionTitle}`}>
				{t('user_data.birthday')}
			</Typography>
			<div data-cy="user_data_birthday_wrapper">
				<div className={classes['.user_data_group_wrapper']}>
					<div id="birthDay" className={classes['.user_data_selector_wrapper']}>
						<GenericSelect
							options={daysSelectValues}
							selectedValue={birthDay}
							setSelectedValue={setBirthDay}
							error={birthDayError}
							placeholder={t('common.date.day')}
							dataCy="user_data_birth_day"
							additionalInputParams={{
								inputRef: birthDayRef,
								onInput: onSelectInput,
							}}
						/>
					</div>
					<div
						id="birthMonth"
						className={classes['.user_data_selector_wrapper']}
					>
						<GenericSelect
							options={monthSelectValues}
							selectedValue={birthMonth}
							setSelectedValue={setBirthMonth}
							error={birthMonthError}
							placeholder={t('common.date.month')}
							dataCy="user_data_birth_month"
							additionalInputParams={{
								inputRef: birthMonthRef,
								onInput: onSelectInput,
							}}
						/>
					</div>
					<div
						id="birthYear"
						className={classes['.user_data_selector_wrapper']}
					>
						<GenericSelect
							options={yearSelectValues}
							selectedValue={birthYear}
							setSelectedValue={onSelectBirthYear}
							error={birthYearError}
							placeholder={t('common.date.year')}
							dataCy="user_data_birth_year"
							additionalInputParams={{
								inputRef: birthYearRef,
							}}
						/>
					</div>
				</div>
				{over18Error && (
					<div className={classes['.user_data_birthday_error']}>
						{over18Error}
					</div>
				)}
			</div>
			<div className={classes['.user_data_price_continue_wrapper']}>
				<NavigationBar onClickContinue={validateAndSubmit} />
			</div>
		</div>
	);
};

interface StateProps {
	contract: ContractState;
}

interface DispatchProps {}

export default UserData;
